import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'gm:install-suggestion:v1';
const SESSION_KEY = 'gm:install-suggestion:session:v1';
const MAX_PROMPTS = 3;
const MAX_EARLY_VISITS = 6;
const REPROMPT_AFTER_MS = 24 * 60 * 60 * 1000;
const isLandingRoute = (pathname) => pathname === '/' || pathname === '/landing';

const readState = () => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
  } catch (_) {
    return {};
  }
};

const writeState = (state) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_) {
    // Installation suggestions are optional; private browsing/storage limits
    // must never interfere with using the site.
  }
};

const isStandalone = () => (
  window.matchMedia?.('(display-mode: standalone)').matches
  || window.navigator.standalone === true
);

const getDevice = () => {
  const userAgent = window.navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/i.test(userAgent)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  return { isIOS, isAndroid: /Android/i.test(userAgent) };
};

const InstallAppPrompt = () => {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [mobile, setMobile] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [device, setDevice] = useState({ isIOS: false, isAndroid: false });
  const [installEvent, setInstallEvent] = useState(null);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px), (pointer: coarse)');
    const updateMobile = () => setMobile(media.matches);
    updateMobile();
    media.addEventListener?.('change', updateMobile);
    return () => media.removeEventListener?.('change', updateMobile);
  }, []);

  useEffect(() => {
    const captureInstallPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };
    const markInstalled = () => {
      writeState({ ...readState(), installed: true });
      setVisible(false);
      setInstallEvent(null);
    };
    window.addEventListener('beforeinstallprompt', captureInstallPrompt);
    window.addEventListener('appinstalled', markInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', captureInstallPrompt);
      window.removeEventListener('appinstalled', markInstalled);
    };
  }, []);

  useEffect(() => {
    if (!isLandingRoute(pathname)) {
      setVisible(false);
      setShowInstructions(false);
      return undefined;
    }
    if (!mobile || isStandalone()) return undefined;

    const currentDevice = getDevice();
    setDevice(currentDevice);

    let state = readState();
    if (state.installed || state.dismissedPermanently) return undefined;

    let alreadyCountedThisSession = false;
    try {
      alreadyCountedThisSession = window.sessionStorage.getItem(SESSION_KEY) === '1';
      if (!alreadyCountedThisSession) {
        window.sessionStorage.setItem(SESSION_KEY, '1');
        state = { ...state, visits: (Number(state.visits) || 0) + 1 };
        writeState(state);
      }
    } catch (_) {
      state = { ...state, visits: (Number(state.visits) || 0) + 1 };
    }

    const visits = Number(state.visits) || 0;
    const prompts = Number(state.prompts) || 0;
    const nextPromptAt = Number(state.nextPromptAt) || 0;
    if (visits > MAX_EARLY_VISITS || prompts >= MAX_PROMPTS || Date.now() < nextPromptAt) {
      return undefined;
    }

    // Let the visitor see the site first; the prompt is a suggestion, not a
    // gate to browsing or creating an account.
    const timeout = window.setTimeout(() => {
      const latestState = readState();
      if (latestState.installed || latestState.dismissedPermanently) return;
      if ((Number(latestState.prompts) || 0) >= MAX_PROMPTS) return;
      const promptState = {
        ...latestState,
        prompts: (Number(latestState.prompts) || 0) + 1,
        nextPromptAt: Date.now() + REPROMPT_AFTER_MS,
      };
      writeState(promptState);
      setVisible(true);
    }, 9000);

    return () => window.clearTimeout(timeout);
  }, [mobile, pathname]);

  const dismiss = useCallback((permanently = false) => {
    const state = readState();
    writeState({
      ...state,
      ...(permanently ? { dismissedPermanently: true } : { nextPromptAt: Date.now() + REPROMPT_AFTER_MS }),
    });
    setVisible(false);
    setShowInstructions(false);
  }, []);

  const install = useCallback(async () => {
    if (!installEvent) {
      setShowInstructions(true);
      return;
    }

    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      setInstallEvent(null);
      if (choice?.outcome === 'accepted') {
        writeState({ ...readState(), installed: true });
        setVisible(false);
      } else {
        dismiss();
      }
    } catch (_) {
      setShowInstructions(true);
    }
  }, [dismiss, installEvent]);

  if (!mobile || !visible || isStandalone() || !isLandingRoute(pathname)) return null;

  const manualInstructions = device.isIOS
    ? t('No iPhone, toca em Partilhar e escolhe “Adicionar ao ecrã principal”.')
    : device.isAndroid
      ? t('No menu do navegador, escolhe “Instalar aplicação” ou “Adicionar ao ecrã principal”.')
      : t('Abre o menu do navegador e escolhe a opção para instalar ou adicionar ao ecrã principal.');

  return (
    <aside className="gm-install-suggestion" role="dialog" aria-modal="false" aria-labelledby="gm-install-title">
      <div className="gm-install-suggestion__icon" aria-hidden="true">
        <img src="/web-app-manifest-192x192.png" alt="" />
      </div>
      <div className="gm-install-suggestion__content">
        <button
          type="button"
          className="gm-install-suggestion__close"
          aria-label={t('Fechar')}
          onClick={() => dismiss()}
        >
          ×
        </button>
        <p className="gm-install-suggestion__eyebrow">Globe Memories</p>
        <h2 id="gm-install-title">{showInstructions ? t('Adicionar ao ecrã principal') : t('Leva a Globe Memories contigo')}</h2>
        <p className="gm-install-suggestion__description">
          {showInstructions
            ? manualInstructions
            : t('Instala a aplicação para aceder mais rapidamente às tuas viagens e partilhar novas experiências.')}
        </p>
        <div className="gm-install-suggestion__actions">
          {showInstructions ? (
            <button type="button" className="gm-install-suggestion__primary" onClick={() => dismiss()}>
              {t('Entendi')}
            </button>
          ) : (
            <button type="button" className="gm-install-suggestion__primary" onClick={install}>
              {t('Instalar aplicação')}
            </button>
          )}
          {!showInstructions && (
            <button type="button" className="gm-install-suggestion__secondary" onClick={() => dismiss()}>
              {t('Agora não')}
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default InstallAppPrompt;
